import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/api.service';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

const FormContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #0084ff;
    box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #0084ff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0073e6;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #ffebee;
  border-radius: 4px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #e8f5e9;
  border-radius: 4px;
  text-align: center;
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: 1rem;
`;

const SwitchLink = styled.span`
  color: #0084ff;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PasswordRequirements = styled.ul`
  margin-top: 0.5rem;
  padding-left: 1.5rem;
  font-size: 0.85rem;
  color: #666;
`;

const ResetPasswordForm = (props) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  
  // Get token from URL or props
  const getTokenFromUrl = () => {
    if (isBrowser) {
      const queryParams = new URLSearchParams(window.location.search);
      return queryParams.get('token');
    }
    return null;
  };
  
  // Get token from props or URL
  const tokenFromProps = props?.token;
  
  useEffect(() => {
    // Extract token from URL query parameters or props
    const tokenParam = tokenFromProps || getTokenFromUrl();
    
    if (!tokenParam) {
      setError('Invalid or missing reset token');
      setIsVerifying(false);
      return;
    }
    
    setToken(tokenParam);
    
    // Verify token validity
    const verifyToken = async () => {
      try {
        const response = await authService.verifyResetToken(tokenParam);
        
        if (response.success && response.valid) {
          setIsValidToken(true);
        } else {
          setError('This password reset link is invalid or has expired');
        }
      } catch (error) {
        setError('Failed to verify reset token');
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [tokenFromProps]); // Only depend on tokenFromProps, not the global location
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate form
    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    // Validate password strength
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    // Validate password match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await authService.resetPassword(token, formData.newPassword);
      
      if (response.success) {
        setSuccess('Your password has been reset successfully');
        setFormData({ newPassword: '', confirmPassword: '' });
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          if (props.onSuccess) {
            // Use callback if provided
            props.onSuccess();
          } else if (isBrowser) {
            // Fallback to window location
            window.location.href = '/login';
          }
        }, 3000);
      } else {
        setError(response.error || 'Failed to reset password');
      }
    } catch (error) {
      setError(error.error || 'An error occurred while resetting your password');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isVerifying) {
    return (
      <FormContainer>
        <Title>Reset Password</Title>
        <div style={{ textAlign: 'center' }}>Verifying your reset link...</div>
      </FormContainer>
    );
  }
  
  if (!isValidToken) {
    return (
      <FormContainer>
        <Title>Reset Password</Title>
        <ErrorMessage>{error}</ErrorMessage>
        <SwitchText>
          <SwitchLink onClick={() => {
          if (props.onCancel) {
            props.onCancel();
          } else if (isBrowser) {
            window.location.href = '/login';
          }
        }}>Back to Login</SwitchLink>
        </SwitchText>
      </FormContainer>
    );
  }
  
  return (
    <FormContainer>
      <Title>Reset Password</Title>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="newPassword">New Password</Label>
          <Input
            type="password"
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder="Enter your new password"
            required
          />
          <PasswordRequirements>
            <li>At least 8 characters long</li>
            <li>Include a mix of letters, numbers, and symbols for better security</li>
          </PasswordRequirements>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your new password"
            required
          />
        </FormGroup>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Form>
      
      <SwitchText>
        <SwitchLink onClick={() => {
          if (props.onCancel) {
            props.onCancel();
          } else if (isBrowser) {
            window.location.href = '/login';
          }
        }}>Back to Login</SwitchLink>
      </SwitchText>
    </FormContainer>
  );
};

export default ResetPasswordForm;
